import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useTaskStatuses } from "@/lib/useTaskStatuses";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GanttChartSquare, ListChecks, Search, ExternalLink, ListTodo, Calendar as CalendarIcon, SlidersHorizontal, X, Trash2 } from "lucide-react";

export default function Projects() {
  const qc = useQueryClient();
  const { meta: STATUS_META, list: statusList } = useTaskStatuses();
  const [view, setView] = useState("timeline"); // timeline | list
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkAssign, setShowBulkAssign] = useState(false);

  const { data: teamMembers = [] } = useQuery({ queryKey: ["team"], queryFn: () => api.get("/team").then((r) => r.data), staleTime: 5 * 60 * 1000 });

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["all-tasks"],
    queryFn: () => api.get("/tasks").then((r) => r.data),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/tasks/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["all-tasks"] }); toast.success("Status updated"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Task update failed"),
  });

  const bulkMut = useMutation({
    mutationFn: (body) => api.post("/tasks/bulk", body),
    onSuccess: (r, vars) => {
      const label = vars.action === "delete" ? "deleted" : "updated";
      toast.success(`${r.data.affected} task${r.data.affected === 1 ? "" : "s"} ${label}`);
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ["all-tasks"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Bulk action failed"),
  });

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return tasks.filter((t) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (!needle) return true;
      const hay = [t.title, t.description, t.deal?.name, t.assignee?.name].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [tasks, q, statusFilter]);

  // Group tasks by deal for the timeline view
  const byDeal = useMemo(() => {
    const m = new Map();
    for (const t of filtered) {
      const key = t.deal?.id || "__no_deal__";
      if (!m.has(key)) m.set(key, { deal: t.deal, tasks: [] });
      m.get(key).tasks.push(t);
    }
    return Array.from(m.values());
  }, [filtered]);

  // Compute global date range across all filtered tasks
  const { minDate, days } = useMemo(() => {
    const withDates = filtered.filter((t) => t.start_date || t.due_date);
    if (withDates.length === 0) return { minDate: null, days: 0 };
    const dates = withDates.flatMap((t) => [t.start_date, t.due_date].filter(Boolean)).map((d) => new Date(d));
    const mn = new Date(Math.min(...dates.map((d) => d.getTime())));
    const mx = new Date(Math.max(...dates.map((d) => d.getTime())));
    mn.setDate(mn.getDate() - 2);
    mx.setDate(mx.getDate() + 2);
    const daysCount = Math.max(1, Math.ceil((mx - mn) / 86400000));
    return { minDate: mn, days: daysCount };
  }, [filtered]);

  const isDone = (s) => STATUS_META[s]?.is_terminal || s === "done";

  const totals = useMemo(() => {
    const t = { total: tasks.length, done: 0, in_progress: 0, blocked: 0, overdue: 0 };
    const now = new Date();
    for (const x of tasks) {
      if (isDone(x.status)) t.done += 1;
      else if (x.status === "in_progress") t.in_progress += 1;
      else if (x.status === "blocked") t.blocked += 1;
      if (x.due_date && !isDone(x.status) && new Date(x.due_date) < now) t.overdue += 1;
    }
    return t;
  }, [tasks, STATUS_META]);

  const dayHeaders = useMemo(() => {
    if (!minDate || days <= 0) return [];
    const arr = [];
    const step = Math.max(1, Math.round(days / 10));
    for (let i = 0; i <= days; i += step) {
      const d = new Date(minDate);
      d.setDate(d.getDate() + i);
      arr.push({ pct: (i / days) * 100, label: d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) });
    }
    return arr;
  }, [minDate, days]);

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Project Management</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black">Projects timeline</h1>
          <p className="text-sm text-muted-foreground mt-1">Every deliverable across every deal — filtered to what matters.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/settings?tab=pipeline"
            className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary flex items-center gap-1.5 border border-border rounded-sm px-2.5 py-1.5 transition-colors"
            data-testid="projects-manage-statuses"
            title="Customize task statuses per division"
          >
            <SlidersHorizontal className="h-3 w-3" /> Manage statuses
          </Link>
          <div className="rounded-sm bg-secondary/40 border border-border p-0.5 flex" data-testid="projects-view-toggle">
            <button onClick={() => setView("timeline")} className={`px-3 py-1.5 rounded-sm text-xs transition-colors flex items-center gap-1.5 ${view === "timeline" ? "bg-primary/20 text-primary" : "text-muted-foreground"}`} data-testid="projects-view-timeline">
              <GanttChartSquare className="h-3.5 w-3.5" /> Timeline
            </button>
            <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-sm text-xs transition-colors flex items-center gap-1.5 ${view === "list" ? "bg-primary/20 text-primary" : "text-muted-foreground"}`} data-testid="projects-view-list">
              <ListChecks className="h-3.5 w-3.5" /> List
            </button>
          </div>
        </div>
      </div>

      {/* Stat pills */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatPill label="Total tasks" value={totals.total} testId="stat-total" />
        <StatPill label="In progress" value={totals.in_progress} tint="#38BDF8" testId="stat-inprogress" />
        <StatPill label="Blocked" value={totals.blocked} tint="#F87171" testId="stat-blocked" />
        <StatPill label="Overdue" value={totals.overdue} tint="#F59E0B" testId="stat-overdue" />
        <StatPill label="Completed" value={totals.done} tint="#25D366" testId="stat-completed" />
      </div>

      {/* Filters */}
      <Card className="rounded-sm bg-card border-border p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="projects-search"
            placeholder="Search tasks, deals, assignees…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-sm bg-secondary/40 pl-8"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-sm bg-secondary/40 w-40" data-testid="projects-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statusList.map((s) => (
              <SelectItem key={s.key} value={s.key} data-testid={`projects-status-filter-${s.key}`}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(q || statusFilter !== "all") && (
          <Button variant="ghost" size="sm" className="rounded-sm text-xs" onClick={() => { setQ(""); setStatusFilter("all"); }} data-testid="projects-clear-filters">
            Clear
          </Button>
        )}
      </Card>

      {isLoading && <div className="text-sm text-muted-foreground text-center py-10">Loading tasks…</div>}

      {!isLoading && filtered.length === 0 && (
        <Card className="rounded-sm bg-card border-border p-12 text-center">
          <ListTodo className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-display text-xl font-extrabold mb-1">No tasks match</div>
          <div className="text-sm text-muted-foreground">
            {tasks.length === 0 ? "Open a deal and add tasks to see them appear here." : "Try broadening your search or status filter."}
          </div>
        </Card>
      )}

      {!isLoading && filtered.length > 0 && view === "timeline" && (
        <Card className="rounded-sm bg-card border-border p-6 overflow-x-auto" data-testid="projects-gantt">
          <div className="min-w-[720px] space-y-6">
            {/* Shared day axis */}
            <div className="flex items-center gap-2 sticky top-0">
              <div className="w-60 flex-none" />
              <div className="flex-1 h-6 relative border-b border-border">
                {dayHeaders.map((h, i) => (
                  <div key={i} className="absolute text-[10px] uppercase tracking-widest text-muted-foreground -translate-x-1/2" style={{ left: `${h.pct}%` }}>{h.label}</div>
                ))}
              </div>
            </div>

            {byDeal.map(({ deal, tasks: dealTasks }) => (
              <div key={deal?.id || "__no_deal__"} data-testid={`gantt-deal-${deal?.id || "none"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {deal?.id ? (
                    <Link to={`/deals/${deal.id}`} className="text-sm font-display font-extrabold text-white hover:text-primary transition-colors flex items-center gap-1.5" data-testid={`deal-link-${deal.id}`}>
                      {deal.name}
                      <ExternalLink className="h-3 w-3 opacity-60" />
                    </Link>
                  ) : (
                    <span className="text-sm font-display font-extrabold text-muted-foreground">Orphaned tasks</span>
                  )}
                  <span className="text-[10px] text-muted-foreground">{dealTasks.length} task{dealTasks.length === 1 ? "" : "s"}</span>
                  {(deal?.tags || []).slice(0, 3).map((tag) => (
                    <Badge key={tag} className="rounded-sm text-[10px] px-1.5 py-0 bg-secondary/60 text-muted-foreground border-border">{tag}</Badge>
                  ))}
                </div>

                <div className="space-y-1.5">
                  {dealTasks.map((t) => (
                    <GanttRow
                      key={t.id}
                      task={t}
                      minDate={minDate}
                      days={days}
                      statusMeta={STATUS_META}
                      statusList={statusList}
                      onChangeStatus={(v) => patchMut.mutate({ id: t.id, body: { status: v } })}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!isLoading && filtered.length > 0 && view === "list" && (
        <>
          {selectedIds.size > 0 && (
            <Card className="rounded-sm bg-primary/10 border-primary/40 p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
              <span className="text-sm font-bold text-primary">{selectedIds.size} selected</span>
              <Select onValueChange={(v) => bulkMut.mutate({ task_ids: Array.from(selectedIds), action: "update", status: v })}>
                <SelectTrigger className="rounded-sm bg-background w-40 h-8 text-xs" data-testid="bulk-status-picker"><SelectValue placeholder="Change status…" /></SelectTrigger>
                <SelectContent>
                  {statusList.map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`bulk-status-${s.key}`}>
                      <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full inline-block" style={{ background: s.color }} />{s.label}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select onValueChange={(v) => bulkMut.mutate({ task_ids: Array.from(selectedIds), action: "update", assigned_to: v === "unassigned" ? "" : v })}>
                <SelectTrigger className="rounded-sm bg-background w-44 h-8 text-xs" data-testid="bulk-assign-picker"><SelectValue placeholder="Reassign…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassign</SelectItem>
                  {teamMembers.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`bulk-assign-${m.id}`}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-sm text-red-400 hover:bg-red-500/10 hover:text-red-400"
                onClick={() => {
                  if (window.confirm(`Delete ${selectedIds.size} task(s)? This cannot be undone.`)) {
                    bulkMut.mutate({ task_ids: Array.from(selectedIds), action: "delete" });
                  }
                }}
                data-testid="bulk-delete"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete
              </Button>
              <div className="flex-1" />
              <button onClick={() => setSelectedIds(new Set())} className="text-muted-foreground hover:text-white" data-testid="bulk-clear"><X className="h-4 w-4" /></button>
            </Card>
          )}
        <Card className="rounded-sm bg-card border-border p-0 overflow-hidden" data-testid="projects-list">
          <div className="hidden md:grid grid-cols-[32px_2fr_1.4fr_1fr_1fr_1fr_1fr] gap-3 px-4 py-3 border-b border-border bg-secondary/40 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            <div className="flex items-center">
              <Checkbox
                checked={filtered.length > 0 && filtered.every((t) => selectedIds.has(t.id))}
                onCheckedChange={(v) => {
                  if (v) setSelectedIds(new Set(filtered.map((t) => t.id)));
                  else setSelectedIds(new Set());
                }}
                data-testid="projects-select-all"
              />
            </div>
            <div>Task</div>
            <div>Deal</div>
            <div>Assignee</div>
            <div>Status</div>
            <div>Start</div>
            <div>Due</div>
          </div>
          <div className="divide-y divide-border">
            {filtered.map((t) => {
              const meta = STATUS_META[t.status] || STATUS_META.todo;
              const overdue = t.due_date && !isDone(t.status) && new Date(t.due_date) < new Date();
              const isSelected = selectedIds.has(t.id);
              return (
                <div key={t.id} className={`grid grid-cols-1 md:grid-cols-[32px_2fr_1.4fr_1fr_1fr_1fr_1fr] gap-3 px-4 py-3 hover:bg-secondary/30 transition-colors ${isSelected ? "bg-primary/5" : ""}`} data-testid={`project-list-row-${t.id}`}>
                  <div className="flex items-center">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelect(t.id)}
                      data-testid={`project-select-${t.id}`}
                    />
                  </div>
                  <div className={`text-sm ${isDone(t.status) ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {t.deal?.id ? (
                      <Link to={`/deals/${t.deal.id}`} className="hover:text-primary transition-colors">{t.deal.name}</Link>
                    ) : "—"}
                  </div>
                  <div className="text-xs text-muted-foreground">{t.assignee?.name || "—"}</div>
                  <div>
                    <Select value={t.status} onValueChange={(v) => patchMut.mutate({ id: t.id, body: { status: v } })}>
                      <SelectTrigger
                        className="rounded-sm h-7 text-[11px] px-2 border-0 hover:opacity-80 transition-opacity"
                        style={{ backgroundColor: meta.bg, color: meta.color }}
                        data-testid={`project-status-${t.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusList.map((s) => (
                          <SelectItem key={s.key} value={s.key} data-testid={`project-status-option-${t.id}-${s.key}`}>
                            <div className="flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full inline-block" style={{ background: s.color }} />
                              {s.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-xs text-muted-foreground">{t.start_date ? new Date(t.start_date).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—"}</div>
                  <div className={`text-xs flex items-center gap-1 ${overdue ? "text-red-400" : "text-muted-foreground"}`}>
                    {t.due_date ? (
                      <>
                        <CalendarIcon className="h-3 w-3" />
                        {new Date(t.due_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                        {overdue ? " · overdue" : ""}
                      </>
                    ) : "—"}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
        </>
      )}
    </div>
  );
}

function StatPill({ label, value, tint, testId }) {
  return (
    <Card className="rounded-sm bg-card border-border p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className="text-3xl font-display font-black mt-1" style={tint ? { color: tint } : {}}>{value}</div>
    </Card>
  );
}

function GanttRow({ task, minDate, days, statusMeta, statusList, onChangeStatus }) {
  const meta = statusMeta[task.status] || statusMeta.todo;
  const s = task.start_date ? new Date(task.start_date) : (task.due_date ? new Date(task.due_date) : null);
  const e = task.due_date ? new Date(task.due_date) : (task.start_date ? new Date(task.start_date) : null);
  const hasDates = !!s && !!e && !!minDate;
  const left = hasDates ? Math.max(0, ((s - minDate) / 86400000 / days) * 100) : 0;
  const width = hasDates ? Math.max(1.5, ((e - s) / 86400000 / days) * 100) : 1.5;
  const isTerminal = meta?.is_terminal;
  const overdue = task.due_date && !isTerminal && new Date(task.due_date) < new Date();

  return (
    <div className="flex items-center gap-2" data-testid={`gantt-task-${task.id}`}>
      <div className="w-60 flex-none flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className={`text-xs truncate ${isTerminal ? "line-through text-muted-foreground" : ""}`}>{task.title}</div>
          <div className="text-[10px] text-muted-foreground truncate">{task.assignee?.name || "Unassigned"}</div>
        </div>
        <Select value={task.status} onValueChange={onChangeStatus}>
          <SelectTrigger
            className="rounded-sm h-6 w-6 p-0 border-0 flex items-center justify-center flex-none"
            style={{ backgroundColor: meta.bg, color: meta.color }}
            aria-label="Change status"
            data-testid={`gantt-status-${task.id}`}
          >
            <span className="h-2 w-2 rounded-full inline-block" style={{ background: meta.color }} />
          </SelectTrigger>
          <SelectContent>
            {statusList.map((s) => (
              <SelectItem key={s.key} value={s.key} data-testid={`gantt-status-option-${task.id}-${s.key}`}>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full inline-block" style={{ background: s.color }} />
                  {s.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 h-7 relative bg-secondary/30 rounded-sm">
        {hasDates ? (
          <div
            className="absolute top-0 h-full rounded-sm flex items-center px-2 text-[10px] font-semibold overflow-hidden"
            style={{
              left: `${left}%`,
              width: `${width}%`,
              backgroundColor: meta.bg,
              borderLeft: `2px solid ${overdue ? "#F87171" : meta.color}`,
              color: overdue ? "#F87171" : meta.color,
            }}
            title={`${task.start_date || "?"} → ${task.due_date || "?"}`}
          >
            {meta.label}{overdue ? " · overdue" : ""}
          </div>
        ) : (
          <div className="absolute inset-y-1/2 left-2 -translate-y-1/2 text-[10px] text-muted-foreground">No dates set</div>
        )}
      </div>
    </div>
  );
}
